import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useGetUsersData } from '../../../hooks';
import { generateUrl, paths } from '../../../lib';
import {
  ExpressPalletSession,
  expressSessionChanged,
  readExpressSession,
} from './session';

/** Server sessions survive navigation, reloads, devices, and sign-out/sign-in. */
export function ExpressPalletGuard() {
  const { userId, token, permissionClaims, isAdmin } = useGetUsersData();
  const [session, setSession] = useState<ExpressPalletSession | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const version = useRef(0);
  const canProcess =
    isAdmin ||
    permissionClaims?.some((p) =>
      ['processfulfilledservice', 'processorder'].includes(p?.toLowerCase()),
    );
  const refresh = useCallback(async () => {
    const request = ++version.current;
    const { data, error } = await readExpressSession();
    if (request === version.current && !error && data) setSession(data.session);
  }, []);
  useEffect(() => {
    setSession(null);
    if (!userId || !token || !canProcess) return;
    const requests = version;
    const changed = (event: Event) => {
      ++requests.current;
      setSession((event as CustomEvent<ExpressPalletSession | null>).detail);
    };
    window.addEventListener(expressSessionChanged, changed);
    window.addEventListener('focus', refresh);
    return () => {
      ++requests.current;
      window.removeEventListener('focus', refresh);
      window.removeEventListener(expressSessionChanged, changed);
    };
  }, [userId, token, canProcess, refresh]);
  // Restore on login, navigation, or refocusing this tab. The express page
  // supplies live progress; idle desktop tabs do not need background polling.
  useEffect(() => {
    if (userId && token && canProcess) void refresh();
  }, [userId, token, canProcess, location.pathname, refresh]);
  useEffect(() => {
    const target = generateUrl(paths.EXPRESS_PICK_AND_STICK);
    // Authentication routes must stay reachable when a token expires.
    if (
      session &&
      token &&
      ![generateUrl(paths.LOGIN), generateUrl(paths.MF_AUTH)].includes(
        location.pathname,
      ) &&
      location.pathname !== target
    )
      navigate(target, { replace: true });
  }, [session, token, location.pathname, navigate]);
  useEffect(() => {
    if (!session) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Browser compatibility fallback alongside preventDefault.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [session]);
  return null;
}
